import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useMetaTags from '../../hooks/useMetaTags';
import {
  Box,
  Container,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  Paper,
  Avatar,
  CircularProgress,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PageHero from '../../components/PageHero/PageHero';
import Footer from '../../components/Footer/Footer';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Lottie from 'lottie-react';
import { 
  fetchWordPressPosts, 
  fetchWordPressCategories, 
  transformWordPressPostToBlogPost,
  BlogPost,
  WordPressCategory,
  WordPressPost
} from '../../services/wordpressApi';

// Styled Components
const BlogContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8f9fa',
  minHeight: '100vh',
  paddingTop: theme.spacing(12),
  paddingBottom: 0
}));

const SearchBar = styled(TextField)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '30px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '30px',
    '& fieldset': {
      borderColor: 'rgba(69, 91, 65, 0.2)',
    },
    '&:hover fieldset': {
      borderColor: '#455b41',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#455b41',
    },
  },
}));

const BlogCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(0,0,0,0.05)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(69, 91, 65, 0.15)',
    '& .blog-image': {
      transform: 'scale(1.05)',
    },
    '& .read-more-arrow': {
      transform: 'translateX(4px)',
    }
  }
}));

const BlogImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: 240,
  overflow: 'hidden'
}));

const BlogImage = styled(CardMedia)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.5s ease',
  position: 'relative'
}));

const DateOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  left: 0,
  backgroundColor: '#ffffff',
  color: '#455b41',
  padding: theme.spacing(1, 1.5),
  borderTopRightRadius: '8px',
  borderBottomRightRadius: '8px',
  fontSize: '0.75rem',
  fontWeight: 600,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  zIndex: 2
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  backgroundColor: '#455b41',
  color: '#ffffff',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: '#5a7054',
  }
}));

const ReadMoreButton = styled(Button)(({ theme }) => ({
  color: '#455b41',
  fontWeight: 600,
  textTransform: 'none',
  padding: '8px 16px',
  '&:hover': {
    backgroundColor: 'rgba(69, 91, 65, 0.05)',
  }
}));

const SidebarSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '16px',
  marginBottom: theme.spacing(3),
  border: '1px solid rgba(69, 91, 65, 0.1)',
  backgroundColor: '#ffffff'
}));

const SidebarTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: '#455b41',
  marginBottom: theme.spacing(2),
  fontSize: '1.3rem',
  fontFamily: '"Lora", serif'
}));

const CategoryButton = styled(Button)(({ theme }) => ({
  justifyContent: 'space-between',
  textTransform: 'none',
  color: '#666',
  padding: '12px 16px',
  marginBottom: '8px',
  backgroundColor: 'rgba(69, 91, 65, 0.05)',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: 'rgba(69, 91, 65, 0.1)',
    color: '#455b41',
  },
  '&.active': {
    backgroundColor: '#455b41',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#5a7054',
    }
  }
}));

const TagButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: '20px',
  margin: '4px',
  padding: '6px 16px',
  border: '1px solid rgba(69, 91, 65, 0.3)',
  color: '#455b41',
  fontSize: '0.85rem',
  '&:hover': {
    backgroundColor: '#455b41',
    color: '#ffffff',
    borderColor: '#455b41',
  }
}));

const TrendingPost = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  cursor: 'pointer',
  padding: theme.spacing(1),
  borderRadius: '8px',
  transition: 'background-color 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(69, 91, 65, 0.05)',
  }
}));

const PaginationContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '4px',
  padding: '12px 20px',
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  border: '1px solid rgba(69, 91, 65, 0.15)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
}));

const PaginationButton = styled(Button)(({ theme }) => ({
  minWidth: '40px',
  height: '40px',
  padding: '0',
  borderRadius: '8px',
  color: '#666',
  backgroundColor: 'transparent',
  fontWeight: 500,
  fontSize: '14px',
  border: 'none',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(69, 91, 65, 0.08)',
    color: '#455b41',
  },
  '&.active': {
    backgroundColor: '#455b41',
    color: '#ffffff',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#5a7054',
      color: '#ffffff',
    }
  },
  '& .MuiSvgIcon-root': {
    fontSize: '20px'
  }
}));

const PaginationEllipsis = styled(Typography)(({ theme }) => ({
  padding: '0 8px',
  color: '#999',
  fontSize: '14px',
  userSelect: 'none'
}));

// Helper function to parse Spanish date format
const parseSpanishDate = (dateStr: string): Date => {
  const months: { [key: string]: number } = {
    'enero': 0, 'febrero': 1, 'marzo': 2, 'abril': 3,
    'mayo': 4, 'junio': 5, 'julio': 6, 'agosto': 7,
    'septiembre': 8, 'octubre': 9, 'noviembre': 10, 'diciembre': 11
  };
  
  // Handle format like "noviembre del 2020" or "15 de marzo del 2021"
  const cleanDate = dateStr.toLowerCase().replace('del ', '').replace('de ', '');
  const parts = cleanDate.split(' ');
  
  let day = 1;
  let month = 0;
  let year = new Date().getFullYear();
  
  if (parts.length === 2) {
    // Format: "noviembre 2020"
    const monthName = parts[0];
    year = parseInt(parts[1], 10);
    month = months[monthName] ?? 0;
  } else if (parts.length >= 3) {
    // Format: "15 marzo 2021"
    day = parseInt(parts[0], 10) || 1;
    const monthName = parts[1];
    year = parseInt(parts[parts.length - 1], 10);
    month = months[monthName] ?? 0;
  }
  
  return new Date(year, month, day);
};

const NewsletterCTA = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
  paddingTop: theme.spacing(15), // Reset padding since wave will handle spacing
  paddingBottom: theme.spacing(1.5),
  paddingLeft: 0,
  paddingRight: 0,
  position: 'relative',
  overflow: 'hidden',
  width: '100vw',
  marginLeft: 'calc(-50vw + 50%)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 50%, rgba(69, 91, 65, 0.05) 0%, transparent 50%)',
    pointerEvents: 'none'
  },
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(1.2)
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(1)
  }
}));

// Wave at the top (inverted from FinalCTASection)
const WaveTop = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '120px',
  zIndex: 10,
  overflow: 'hidden',
  '& svg': {
    width: '100%',
    height: '100%',
    display: 'block'
  },
  [theme.breakpoints.down('lg')]: {
    height: '100px'
  },
  [theme.breakpoints.down('md')]: {
    height: '80px'
  },
  [theme.breakpoints.down('sm')]: {
    height: '60px'
  },
  '@media (max-width: 480px)': {
    height: '50px'
  }
}));


const NewsletterContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    textAlign: 'center',
    gap: theme.spacing(3)
  }
}));

const NewsletterText = styled(Box)(({ theme }) => ({
  flex: '1 1 45%',
  maxWidth: '500px',
  transform: 'translateX(-30px)',
  [theme.breakpoints.down('md')]: {
    flex: '1 1 100%',
    maxWidth: '600px',
    transform: 'none'
  }
}));

const NewsletterAnimation = styled(Box)(({ theme }) => ({
  flex: '0 0 600px',
  height: '500px',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  marginTop: '-100px',
  [theme.breakpoints.down('md')]: {
    flex: '0 0 450px',
    height: '400px',
    order: -1,
    alignItems: 'center',
    marginTop: 0
  },
  [theme.breakpoints.down('sm')]: {
    flex: '0 0 350px',
    height: '300px',
    alignItems: 'center',
    marginTop: 0
  }
}));

const EmailForm = styled('form')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(1.5)
  }
}));

const EmailInput = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '30px',
    fontSize: '1rem',
    '& fieldset': {
      borderColor: 'rgba(69, 91, 65, 0.3)',
    },
    '&:hover fieldset': {
      borderColor: '#455b41',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#455b41',
      borderWidth: '2px'
    },
  },
  '& .MuiInputBase-input': {
    padding: '14px 20px',
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiInputBase-input': {
      padding: '12px 16px',
    }
  }
}));

const CTAButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#455b41',
  color: '#ffffff',
  padding: '14px 32px',
  fontSize: '1.1rem',
  fontWeight: 600,
  borderRadius: '30px',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 15px rgba(69, 91, 65, 0.2)',
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: '#5a7054',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(69, 91, 65, 0.3)'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    padding: '12px 24px'
  }
}));

const LoadingSpinner = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '400px',
  color: '#455b41'
}));

const NoticiasBlog: React.FC = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<WordPressCategory[]>([]);
  const [wpPosts, setWpPosts] = useState<WordPressPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [animationData, setAnimationData] = useState<any>(null);
  const [email, setEmail] = useState('');

  // Update meta tags for blog page
  useMetaTags({
    title: 'Blog de Agricultura Ecológica - EKKOFOOD',
    description: 'Descubre artículos sobre agricultura ecológica, permacultura, sostenibilidad y técnicas de cultivo orgánico. Noticias y consejos de expertos en agricultura regenerativa.',
    image: 'https://ekkofood.com/organic_farm.png',
    url: 'https://ekkofood.com/noticias-blog',
    type: 'website'
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [wpPosts, wpCategories] = await Promise.all([
          fetchWordPressPosts(),
          fetchWordPressCategories()
        ]);

        const transformedPosts = wpPosts.map(post => 
          transformWordPressPostToBlogPost(post, wpCategories)
        );

        // Sort posts by date (newest first)
        const sortedPosts = transformedPosts.sort((a, b) => {
          const dateA = parseSpanishDate(a.date);
          const dateB = parseSpanishDate(b.date);
          return dateB.getTime() - dateA.getTime(); // Descending order (newest first)
        });

        setPosts(sortedPosts);
        setFilteredPosts(sortedPosts);
        setCategories(wpCategories);
        setWpPosts(wpPosts);
      } catch (err) {
        setError('Error al cargar las publicaciones. Por favor, intenta de nuevo.');
        console.error('Error loading blog data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    fetch('/farmer-animation.json')
      .then(response => response.json())
      .then(data => setAnimationData(data))
      .catch(error => console.error('Error loading animation:', error));
  }, []);

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    filterPosts(searchTerm, category);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const term = event.target.value.toLowerCase();
    setSearchTerm(term);
    filterPosts(term, selectedCategory);
  };

  const filterPosts = (searchTerm: string, category: string) => {
    let filtered = posts;

    if (category !== 'Todos') {
      // Filter posts based on original WordPress categories (handles multiple categories per post)
      const selectedCategoryId = categories.find(cat => cat.name === category)?.id;
      if (selectedCategoryId) {
        filtered = filtered.filter(post => {
          const wpPost = wpPosts.find(wp => wp.id === post.id);
          return wpPost?.categories.includes(selectedCategoryId);
        });
      }
    }

    if (searchTerm) {
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(searchTerm) ||
        post.excerpt.toLowerCase().includes(searchTerm) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchTerm))
      );
    }

    setFilteredPosts(filtered);
    setCurrentPage(1);
  };

  const postsPerPage = 6;
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const currentPosts = filteredPosts.slice(startIndex, startIndex + postsPerPage);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getAllTags = (): string[] => {
    const tagSet = new Set<string>();
    posts.forEach(post => {
      post.tags.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).slice(0, 12);
  };

  const getCategoriesWithCounts = () => {
    const categoryCounts = new Map<string, number>();
    categoryCounts.set('Todos', posts.length);
    
    // Count posts for each category based on original WordPress categories (multiple categories per post)
    categories.forEach(category => {
      const postsInCategory = wpPosts.filter(wpPost => 
        wpPost.categories.includes(category.id)
      ).length;
      categoryCounts.set(category.name, postsInCategory);
    });

    return Array.from(categoryCounts.entries()).map(([name, count]) => ({
      name,
      count
    }));
  };

  const getTrendingPosts = () => {
    return posts.slice(0, 3).map((post, index) => ({
      id: post.id,
      slug: post.slug,
      title: post.title.length > 40 ? post.title.substring(0, 40) + '...' : post.title,
      views: `${Math.floor(Math.random() * 3) + 1}.${Math.floor(Math.random() * 10)}k`
    }));
  };

  const handleEmailSubscription = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      // Handle email subscription logic here
      console.log('Subscribing email:', email);
      alert(`¡Gracias por suscribirte! Te hemos enviado un correo de confirmación a ${email}`);
      setEmail('');
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const showEllipsisStart = currentPage > 4;
    const showEllipsisEnd = currentPage < totalPages - 3;

    if (totalPages <= 7) {
      // Show all pages if total is 7 or less
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (showEllipsisStart) {
        pages.push('...');
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (showEllipsisEnd) {
        pages.push('...');
      }

      // Always show last page
      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <>
      <PageHero
        title="Noticias & Blog"
        subtitle="Explora las últimas tendencias en agricultura sostenible"
        backgroundImage="https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?w=1920&h=600&fit=crop"
        showBreadcrumbs={true}
        breadcrumbs={[
          { label: 'Inicio', link: '/' },
          { label: 'Noticias & Blog', link: '/noticias-blog' }
        ]}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <BlogContainer>
        <Container maxWidth="lg">
          {/* Search Bar */}
          <Box sx={{ mb: 6 }}>
            <SearchBar
              fullWidth
              placeholder="Buscar artículos..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#455b41' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {loading && (
            <LoadingSpinner>
              <CircularProgress size={60} sx={{ color: '#455b41' }} />
            </LoadingSpinner>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && (
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {/* Main Content - Blog Posts */}
              <Box sx={{ flex: '1 1 66%', minWidth: { xs: '100%', md: '500px' } }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {currentPosts.map((post) => (
                    <Box key={post.id} sx={{ flex: '1 1 calc(50% - 12px)', minWidth: { xs: '100%', sm: '280px' } }}>
                      <BlogCard>
                      <BlogImageContainer 
                        onClick={() => navigate(`/${post.slug}`)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <BlogImage
                          className="blog-image"
                          image={post.image}
                          title={post.title}
                        />
                        <DateOverlay>
                          {post.date}
                        </DateOverlay>
                      </BlogImageContainer>
                      <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                        {/* Author */}
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ 
                            fontSize: '0.8rem',
                            mb: 1.5,
                            display: 'block',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5
                          }}
                        >
                          By{' '}
                          <span style={{ color: '#455b41', fontWeight: 700 }}>
                            {post.author}
                          </span>
                        </Typography>
                        
                        {/* Title */}
                        <Typography
                          gutterBottom
                          variant="h6"
                          component="h2"
                          sx={{
                            fontWeight: 700,
                            color: '#2a3a28',
                            fontSize: '1.2rem',
                            lineHeight: 1.3,
                            mb: 1.5,
                            fontFamily: '"Lora", serif',
                            cursor: 'pointer',
                            transition: 'color 0.3s ease',
                            '&:hover': {
                              color: '#455b41'
                            }
                          }}
                          onClick={() => navigate(`/${post.slug}`)}
                        >
                          {post.title}
                        </Typography>
                        
                        {/* Description */}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2.5,
                            lineHeight: 1.6,
                            fontSize: '0.9rem'
                          }}
                        >
                          {post.excerpt}
                        </Typography>
                      </CardContent>
                      
                      <CardActions sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                        <ReadMoreButton
                          endIcon={<ArrowForwardIcon className="read-more-arrow" sx={{ transition: 'transform 0.3s ease' }} />}
                          onClick={() => navigate(`/${post.slug}`)}
                        >
                          Leer Más →
                        </ReadMoreButton>
                      </CardActions>
                    </BlogCard>
                  </Box>
                ))}
              </Box>
              
              {/* Pagination */}
              <Box sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
                <PaginationContainer>
                  <PaginationButton
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    sx={{
                      '&.Mui-disabled': {
                        opacity: 0.3,
                        cursor: 'not-allowed'
                      }
                    }}
                  >
                    <ChevronLeftIcon />
                  </PaginationButton>
                  
                  {getPageNumbers().map((page, index) => (
                    typeof page === 'number' ? (
                      <PaginationButton
                        key={index}
                        onClick={() => handlePageChange(page)}
                        className={currentPage === page ? 'active' : ''}
                      >
                        {page}
                      </PaginationButton>
                    ) : (
                      <PaginationEllipsis key={index}>{page}</PaginationEllipsis>
                    )
                  ))}
                  
                  <PaginationButton
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    sx={{
                      '&.Mui-disabled': {
                        opacity: 0.3,
                        cursor: 'not-allowed'
                      }
                    }}
                  >
                    <ChevronRightIcon />
                  </PaginationButton>
                </PaginationContainer>
              </Box>
            </Box>

              {/* Sidebar */}
              <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: '350px' } }}>
                {/* Categories */}
                <SidebarSection elevation={0}>
                  <SidebarTitle>Categorías</SidebarTitle>
                  <Stack>
                    {getCategoriesWithCounts().map((category) => (
                      <CategoryButton
                        key={category.name}
                        fullWidth
                        className={selectedCategory === category.name ? 'active' : ''}
                        onClick={() => handleCategoryClick(category.name)}
                      >
                        <span>{category.name}</span>
                        <span style={{ opacity: 0.7 }}>({category.count})</span>
                      </CategoryButton>
                    ))}
                  </Stack>
                </SidebarSection>

                {/* Popular Tags */}
                <SidebarSection elevation={0}>
                  <SidebarTitle>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <LocalOfferIcon sx={{ color: '#455b41' }} />
                      <span>Etiquetas Populares</span>
                    </Stack>
                  </SidebarTitle>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', margin: '-4px' }}>
                    {getAllTags().map((tag) => (
                      <TagButton key={tag} size="small">
                        {tag}
                      </TagButton>
                    ))}
                  </Box>
                </SidebarSection>

                {/* Trending Posts */}
                <SidebarSection elevation={0}>
                  <SidebarTitle>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <TrendingUpIcon sx={{ color: '#455b41' }} />
                      <span>Artículos Populares</span>
                    </Stack>
                  </SidebarTitle>
                  {getTrendingPosts().map((post, index) => (
                    <TrendingPost key={post.id} onClick={() => navigate(`/${post.slug}`)}>
                    <Avatar
                      sx={{
                        bgcolor: '#455b41',
                        width: 32,
                        height: 32,
                        fontSize: '0.9rem',
                        mr: 2
                      }}
                    >
                      {index + 1}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: '#2a3a28',
                          lineHeight: 1.3,
                          mb: 0.5
                        }}
                      >
                        {post.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {post.views} vistas
                      </Typography>
                    </Box>
                  </TrendingPost>
                ))}
              </SidebarSection>

              {/* Newsletter Signup */}
              <SidebarSection
                elevation={0}
                sx={{
                  background: 'linear-gradient(135deg, #455b41 0%, #5a7054 100%)',
                  color: '#ffffff'
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    fontFamily: '"Lora", serif'
                  }}
                >
                  Suscríbete a nuestro Newsletter
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, opacity: 0.95 }}>
                  Recibe las últimas noticias y consejos sobre agricultura sostenible directamente en tu correo.
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Tu email"
                  sx={{
                    mb: 2,
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-root': {
                      color: '#ffffff',
                      '& fieldset': {
                        borderColor: 'rgba(255,255,255,0.3)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255,255,255,0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#ffffff',
                      },
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: 'rgba(255,255,255,0.7)',
                      opacity: 1
                    }
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    backgroundColor: '#ffffff',
                    color: '#455b41',
                    fontWeight: 600,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.9)'
                    }
                  }}
                >
                  Suscribirse
                </Button>
                </SidebarSection>
              </Box>
            </Box>
          )}
        </Container>
        </BlogContainer>

        {/* Newsletter CTA Section - Full Width */}
        <NewsletterCTA>
          {/* Smooth wavy top (inverted from FinalCTASection) */}
          <WaveTop>
            <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
              <path 
                d="M0,120 C150,20 300,120 600,70 C900,20 1050,120 1200,70 L1200,0 L0,0 Z" 
                fill="#f8f9fa"
              />
            </svg>
          </WaveTop>
          
          <Container maxWidth="lg">
            <NewsletterContent>
              <NewsletterText>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: '#2a3a28',
                    fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
                    marginBottom: 1,
                    fontFamily: '"Lora", serif',
                    lineHeight: 1.2
                  }}
                >
                  Productos orgánicos directamente del agricultor
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#5a5a5a',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    lineHeight: 1.6,
                    marginBottom: 2
                  }}
                >
                  Únete a nuestra comunidad de más de 10,000 suscriptores y recibe las mejores 
                  ofertas y consejos sobre agricultura sostenible.
                </Typography>
                <EmailForm onSubmit={handleEmailSubscription}>
                  <EmailInput
                    type="email"
                    placeholder="Ingresa tu correo electrónico"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    variant="outlined"
                  />
                  <CTAButton
                    type="submit"
                    variant="contained"
                  >
                    Suscríbete
                  </CTAButton>
                </EmailForm>
              </NewsletterText>
              
              <NewsletterAnimation>
                {animationData ? (
                  <Lottie
                    animationData={animationData}
                    loop={true}
                    autoplay={true}
                    style={{ 
                      width: '100%', 
                      height: '100%',
                      transform: 'scale(1.5)',
                      transformOrigin: 'center',
                      marginTop: '-80px'
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: { xs: '300px', sm: '400px', md: '500px' },
                      height: { xs: '300px', sm: '400px', md: '500px' },
                      borderRadius: '50%',
                      backgroundColor: 'rgba(69, 91, 65, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: { xs: '120px', sm: '160px', md: '200px' }
                    }}
                  >
                    🌾
                  </Box>
                )}
              </NewsletterAnimation>
            </NewsletterContent>
          </Container>
        </NewsletterCTA>

        <Footer />
      </Box>
    </>
  );
};

export default NoticiasBlog;